import React from "react";

// ✅ Use your real file here (keep it inside ../../../assets/)
import heroImg from "../../../assets/billboardQr.webp";

const WHATSAPP_PHONE = "254700000000"; // update (no +)
const whatsappUrl = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
    "Hi! I'm interested in Dynamic QR Codes for Marketing Campaigns."
)}`;

export default function CampaignQrPage() {
    // Smooth scroll (no URL hashes)
    const NAV_H = 72;
    function scrollToId(id) {
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.pageYOffset - NAV_H - 8;
        window.scrollTo({ top, behavior: "smooth" });
    }
    function onNavClick(id) {
        scrollToId(id);
        const toggle = document.getElementById("camp-nav-toggle");
        if (toggle && toggle.checked) toggle.checked = false;
    }

    return (
        <div className="campaigns-page">

            {/* HERO */}
            <section id="home" className="hero">
                <div className="container hero-grid">

                    <div className="hero-copy">
                        <h1>
                            Dynamic QR Codes <span className="accent">for Marketing Campaigns</span>
                        </h1>
                        <p className="lead">
                            Launch, track, and optimize with a single code. Swap destinations anytime, route by location, and
                            measure results—without reprinting.
                        </p>

                        <div className="row gap hero-actions">
                            <button type="button" className="btn primary" onClick={() => onNavClick("pricing")}>
                                See Pricing
                            </button>
                            <button type="button" className="btn ghost" onClick={() => onNavClick("features")}>
                                Explore Features
                            </button>
                            <a href={whatsappUrl} className="btn whats" target="_blank" rel="noreferrer">
                                WhatsApp
                            </a>
                        </div>

                        <ul className="hero-bullets">
                            <li>Swap links post-print (posters, flyers, packaging, OOH)</li>
                            <li>Auto-tag with UTM; view scans by time, device, location</li>
                            <li>A/B test landing pages per city, store, or publisher</li>
                        </ul>
                    </div>


                    {/* On small screens the image appears first (handled in CSS) */}
                    <div className="hero-art">
                        <img
                            src={heroImg}
                            alt="Marketing campaign QR code example"
                            className="hero-img"
                            loading="eager"
                            decoding="async"
                        />
                    </div>
                </div>
            </section>

            {/* USE CASES */}
            <section id="usecases" className="section">
                <div className="container">
                    <h2>Where Dynamic QR Codes Shine</h2>
                    <p className="muted max">Print once, iterate forever. Convert offline attention into measurable actions.</p>

                    <div className="grid usecases">
                        <UseCase title="Posters & Billboards" emoji="🧱">
                            Route by city or time of day. Morning scans to breakfast; evening scans to dinner or happy hour.
                        </UseCase>
                        <UseCase title="Retail & Pop-ups" emoji="🛍️">
                            Store-specific promos, loyalty signup, Google Reviews—swap per week without reprinting.
                        </UseCase>
                        <UseCase title="Events & Sponsorships" emoji="🎟️">
                            Capture leads, trigger WhatsApp flows, distribute slides, and run post-event surveys.
                        </UseCase>
                        <UseCase title="Flyers & Magazines" emoji="📰">
                            Use one printed QR for multiple campaigns over months. Just change the destination.
                        </UseCase>
                        <UseCase title="Transit & OOH" emoji="🚌">
                            Geofencing + UTM for route performance. A/B test copy per region, track cost-per-scan.
                        </UseCase>
                        <UseCase title="Influencers & Radio" emoji="📻">
                            Creator-specific QR variants for precise attribution and ROI.
                        </UseCase>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="features" className="section muted-bg">
                <div className="container">
                    <h2>Features Built for Marketers</h2>

                    <div className="grid features">
                        <Feature emoji="🔁" title="Dynamic Destinations">
                            Change where the code points without reprinting. Schedule by date/time.
                        </Feature>
                        <Feature emoji="🌍" title="Geo-Routing">
                            Nairobi to one page, Mombasa to another—automatically. Optional language routing.
                        </Feature>
                        <Feature emoji="🧪" title="A/B Testing">
                            Split traffic across pages and see winners by scans/conversions.
                        </Feature>
                        <Feature emoji="📈" title="Scan Analytics">
                            Track scans by hour, device, OS, approximate location. Export CSV.
                        </Feature>
                        <Feature emoji="🏷️" title="Auto UTM Tagging">
                            Append UTM parameters for clean attribution in GA or ad platforms.
                        </Feature>
                        <Feature emoji="🔒" title="Anti-Abuse Guards">
                            Throttle suspicious bursts; optional cool-downs; duplicate-pattern flags.
                        </Feature>
                    </div>

                    <div className="cta-slab">
                        <p className="muted">Running a campaign soon? We can set up and design the QR creative for you.</p>
                        <a href={whatsappUrl} className="btn primary" target="_blank" rel="noreferrer">Start a Pilot</a>
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="section">
                <div className="container">
                    <h2>Pricing</h2>
                    <p className="muted max">Flexible tiers. Upgrade or pause anytime.</p>

                    <div className="grid pricing">
                        <div className="price">
                            <h3>Starter</h3>
                            <p className="price-num">KES 3,900 <span>/month</span></p>
                            <ul>
                                <li>3 dynamic QR codes</li>
                                <li>Up to 5k scans / month</li>
                                <li>Basic analytics + UTM</li>
                            </ul>
                            <a href={whatsappUrl} className="btn block whats" target="_blank" rel="noreferrer">Choose Starter</a>
                            <p className="note">Best for quick promos or trials.</p>
                        </div>

                        <div className="price popular" aria-label="Most popular">
                            <div className="badge">Most Popular</div>
                            <h3>Growth</h3>
                            <p className="price-num">KES 9,900 <span>/month</span></p>
                            <ul>
                                <li>10 dynamic QR codes</li>
                                <li>Up to 30k scans / month</li>
                                <li>Geo-routing + A/B testing</li>
                                <li>CSV exports</li>
                            </ul>
                            <a href={whatsappUrl} className="btn primary block" target="_blank" rel="noreferrer">Choose Growth</a>
                            <p className="note">Perfect for multi-channel campaigns.</p>
                        </div>

                        <div className="price">
                            <h3>Enterprise</h3>
                            <p className="price-num">Custom</p>
                            <ul>
                                <li>Unlimited codes & scans</li>
                                <li>SSO + private API</li>
                                <li>Priority support</li>
                                <li>Custom analytics</li>
                            </ul>
                            <a href={whatsappUrl} className="btn block" target="_blank" rel="noreferrer">Contact Sales</a>
                            <p className="note">For national brands & agencies.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="section">
                <div className="container">
                    <h2>FAQ</h2>
                    <div className="faq">
                        <details>
                            <summary>Can I change the QR target after printing?</summary>
                            <p>Yes—update destinations anytime or schedule changes in advance.</p>
                        </details>
                        <details>
                            <summary>Do you support multiple links from one QR?</summary>
                            <p>Yes. Use A/B testing to split traffic or geo-routing to send people to market-specific pages.</p>
                        </details>
                        <details>
                            <summary>Can I track conversions?</summary>
                            <p>We auto-tag visits with UTM so analytics and ad platforms attribute properly.</p>
                        </details>
                        <details>
                            <summary>Can I export my data?</summary>
                            <p>Growth and Enterprise include CSV export of scan logs and aggregates.</p>
                        </details>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="section bottom-cta">
                <div className="container">
                    <h2>Turn offline attention into measurable results</h2>
                    <p className="muted max">Spin up a pilot in days. We’ll handle setup and creative.</p>
                    <div className="row gap center">
                        <a href={whatsappUrl} className="btn primary" target="_blank" rel="noreferrer">Launch a Pilot</a>
                        <button type="button" className="btn ghost" onClick={() => onNavClick("usecases")}>
                            See Use Cases
                        </button>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="footer">
                <div className="container footer-row">
                    <span className="muted tiny">© {new Date().getFullYear()} QR Flex</span>
                    <button type="button" className="tiny linklike" onClick={() => onNavClick("home")}>
                        Back to top ↑
                    </button>
                </div>
            </footer>
        </div>
    );
}

/* Small building blocks */
function UseCase({ emoji, title, children }) {
    return (
        <div className="usecase">
            <div className="usecase-head">
                <span className="emoji" aria-hidden="true">{emoji}</span>
                <h3>{title}</h3>
            </div>
            <p className="muted">{children}</p>
        </div>
    );
}

function Feature({ emoji, title, children }) {
    return (
        <div className="feature">
            <div className="feature-head">
                <span className="emoji" aria-hidden="true">{emoji}</span>
                <h3>{title}</h3>
            </div>
            <p className="muted">{children}</p>
        </div>
    );
}
